import React, {createContext, useContext, useState, useEffect, useRef} from 'react';
import {Client, Room} from 'colyseus.js';
import {Schema, ArraySchema} from '@colyseus/schema';
import {TimepassSDK} from '../assets/images/TimepassAPI/TimepassSDK';

interface EligiblePrize {
    prizeId: string;
    prizeName: string;
}

interface Player {
    id: string;
    name: string;
    profile: string;
    type: number;
    isReady: boolean;
    isOnline: boolean;
    sessionId: string;
    score: number;
    hasWon: boolean;
    ticket: number[][];
    playerId?: string;
    playerName?: string;
}

interface Prize {
    id: string;
    name: string;
    points: number;
    priority: number;
}

interface TicketRow {
    numbers: number[];
}

interface TicketGrid {
    rows: TicketRow[];
}

interface GameState extends Schema {
    playerProfiles: ArraySchema<Player>;
    players: ArraySchema<{
        playerId: string;
        playerName: string;
        profile: string;
        isBot: boolean;
        isReady: boolean;
        isOnline: boolean;
        sessionId: string;
        score: number;
        hasWon: boolean;
        ticket: TicketGrid;
    }>;
    currentNumber: number;
    calledNumbers: ArraySchema<number>;
    isGameStarted: boolean;
    isGameEnded: boolean;
    currentPlayerCount: number;
    roomId: string;
}

interface GameContextType {
    client: Client | null;
    room: Room<GameState> | null;
    players: Player[];
    currentNumber: number | null;
    calledNumbers: number[];
    ticket: TicketGrid | null;
    isReady: boolean;
    hasWon: boolean;
    joinGame: (roomId: string, playerId: string) => Promise<void>;
    markNumber: (number: number) => void;
    claimPrize: (prizeId: string) => void;
    setReady: () => void;
    prizes: Prize[];
    prizeWinners: PrizeWinner[]
    errorMessage: string | null;
    errorCategory: string | null;
    setErrorMessage: (message: string) => void;
    numbersLeft: NumbersLeftEntry[];
    prizeWinner: CurrentPrizeWinner | null;
    setPrizeWinner: (data: CurrentPrizeWinner | null) => void;
    gameOver: boolean;
    eligiblePrizes: EligiblePrize[];
    checkedNumbers: number[];
    setCheckedNumbers: (numbers: number[]) => void;
}

interface PrizeWinner {
    prize: Prize;
    player: Player
}

interface NumbersLeftEntry {
    playerId: string;
    numbersLeft: number;
}

interface CurrentPrizeWinner {
    playerId: string;
    prizeId: string;
}

const GameContext = createContext<GameContextType | undefined>(undefined);

const MAX_RETRIES = 10;

export const GameProvider: React.FC<{ children: React.ReactNode }> = ({children}) => {
    const pingIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
    const [client, setClient] = useState<Client | null>(null);
    const [prizeWinners, setPrizeWinners] = useState<PrizeWinner[]>([]);
    const [room, setRoom] = useState<Room<GameState> | null>(null);
    const [players, setPlayers] = useState<Player[]>([]);
    const [currentNumber, setCurrentNumber] = useState<number | null>(null);
    const [calledNumbers, setCalledNumbers] = useState<number[]>([]);
    const [checkedNumbers, setCheckedNumbers] = useState<number[]>([]);
    const [numbersLeft, setNumbersLeft] = useState<NumbersLeftEntry[]>([]);
    const [ticket, setTicket] = useState<TicketGrid | null>(null);
    const [isReady, setIsReady] = useState(false);
    const [hasWon, setHasWon] = useState(false);
    const [gameOver, setGameOver] = useState<boolean>(false);
    const [prizes, setPrizes] = useState<Prize[]>([]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [errorCategory, setErrorCategory] = useState<string | null>(null);
    const [prizeWinner, setPrizeWinner] = useState<CurrentPrizeWinner | null>(null);
    const retryCount = useRef(0);
    const reconnecting = useRef(false);
    const lastRoomId = useRef<string | null>(null);
    const reconnectionToken = useRef<string | null>(null);
    const lastSessionId = useRef<string | null>(null);
    const [eligiblePrizes, setEligiblePrizes] = useState<EligiblePrize[]>([]);

    const setupRoomListeners = (room: Room<any>) => {
        room.onLeave((code) => {
            console.warn("Room left with code", code);
            attemptReconnection();
        });

        room.onError((code, message) => {
            console.error("Room error", code, message);
            attemptReconnection();
        });
    };
    const attemptReconnection = () => {
        if (reconnecting.current || !client || !lastRoomId.current || !lastSessionId.current) return;

        reconnecting.current = true;
        const tryReconnect = async () => {
            if (retryCount.current >= MAX_RETRIES) {
                console.error("Max reconnection attempts reached.");
                reconnecting.current = false;
                return;
            }

            try {
                console.log(reconnectionToken?.current)
                const rejoinedRoom = await client.reconnect(reconnectionToken.current as string) as Room<GameState>;
                setRoom(rejoinedRoom);
                reconnectionToken.current = rejoinedRoom.reconnectionToken;
                retryCount.current = 0;
                reconnecting.current = false;
                console.log("Reconnected successfully.");
                setupRoomListeners(rejoinedRoom);
            } catch (err) {
                retryCount.current++;
                console.error("Reconnection attempt failed:", err);
                setTimeout(tryReconnect, 1000);
            }

        };

        tryReconnect();
    };
    useEffect(() => {
        // Get roomId and playerId from URL params first
        const urlParams = new URLSearchParams(window.location.search);
        let roomId = urlParams.get('roomId');
        const clientId = urlParams.get('clientId') || "83fec89d-7f33-4777-87a7-a558290b030c";
        const gameId = "bc73054a-cbc5-4112-ac2e-38f45a671045";
        roomId = gameId + "-" + clientId + "-" + roomId;
        const playerId = urlParams.get('playerId');

        console.log("Initializing game client...");
        console.log("URL Parameters:", {roomId, playerId});

        if (!roomId || !playerId) {
            console.error("Missing required URL parameters: roomId and playerId");
            return;
        }

        // Initialize TimepassSDK
        // const timepassSDK = TimepassSDK.getInstance();
        // console.log("TimepassSDK initialized");

        // Initialize client with error handling
        try {
            //wss://dev.tambola-multiplayer.apis.simpleviralgames.com
            const gameClient = new Client('wss://prod.tambola-multiplayer.apis.simpleviralgames.com');
            setClient(gameClient);

            if (window.timepass) {
                const timepassSDK = TimepassSDK.getInstance();
                console.log("TimepassSDK initialized");
            } else {
                console.log("TimepassSDK not initialized");
            }

            // Call PageReady when game is initialized
            // const timepassSDK = TimepassSDK.getInstance();
            // timepassSDK.PageReady();
        } catch (error) {
            console.error("Error creating game client:", error);
        }
    }, []);

    // Separate useEffect to handle joining game after client is initialized
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        let roomId = urlParams.get('roomId');
        const clientId = urlParams.get('clientId') || "83fec89d-7f33-4777-87a7-a558290b030c";
        const gameId = "bc73054a-cbc5-4112-ac2e-38f45a671045";
        roomId = gameId + "-" + clientId + "-" + roomId;
        const playerId = urlParams.get('playerId');

        if (client && roomId && playerId) {
            console.log("Client initialized, attempting to join game...");
            joinGame(roomId, playerId).catch(error => {
                console.error("Error joining game:", error);
            });
        }
    }, [client]);

    const joinGame = async (roomId: string, playerId: string) => {
        console.log("Attempting to join game with client:", client);

        if (!client) {
            console.error("Client is not initialized yet");
            return;
        }

        try {
            console.log("Joining room:", roomId, "with playerId:", playerId);
            const gameRoom = await client.joinById<GameState>(roomId, {playerId});
            reconnectionToken.current = gameRoom.reconnectionToken;
            console.log("Successfully joined room:", gameRoom);
            console.log(reconnectionToken.current)
            lastRoomId.current = roomId;
            lastSessionId.current = gameRoom.sessionId;
            console.log("Joined game:", gameRoom);
            setupRoomListeners(gameRoom);
            setRoom(gameRoom);
        } catch (error) {
            console.error('Error joining game:', error);
        }
    };


    useEffect(() => {
        if (!room) {
            return;
        }
        const urlParams = new URLSearchParams(window.location.search);
        const playerId = urlParams.get('playerId');
        pingIntervalRef.current = setInterval(() => {
            if (room.connection && room.connection.isOpen) {
                room.send("ping");
                console.log("Sent ping to keep connection alive");
            }
        }, 10000);

        room.onError((code, message) => {
            console.error("Room error:", code, message);
        });

        // Listen for state changes
        room.onStateChange((state) => {
            // console.log("=== State Change ===");
            // console.log("Full state:", JSON.stringify(state, null, 2));

            // Update players - use the full players array instead of just playerProfiles
            if (state.players) {
                const playerArray = Array.from(state.players.values());
                // console.log("=== Players Update ===");
                // console.log("Raw player array:", JSON.stringify(playerArray, null, 2));

                // Convert player array to proper format
                const formattedPlayers = playerArray.map(player => {
                    // Convert TicketGrid to 2D grid
                    const ticketGrid: number[][] = [];
                    if (player.ticket && player.ticket.rows) {
                        Array.from(player.ticket.rows).forEach(row => {
                            if (row && row.numbers) {
                                ticketGrid.push(Array.from(row.numbers));
                            }
                        });
                    }

                    const formattedPlayer = {
                        id: player.playerId,
                        name: player.playerName,
                        profile: player.profile,
                        type: player.isBot ? 1 : 0,
                        isReady: player.isReady,
                        isOnline: player.isOnline,
                        sessionId: player.sessionId,
                        score: player.score,
                        hasWon: player.hasWon,
                        ticket: ticketGrid
                    };
                    // console.log("Formatted player:", JSON.stringify(formattedPlayer, null, 2));
                    return formattedPlayer;
                });

                // console.log("Setting players state:", JSON.stringify(formattedPlayers, null, 2));
                setPlayers(formattedPlayers);

                // Find current player's ticket
                const currentPlayer = formattedPlayers.find(p => p.id === playerId);
                if (currentPlayer && currentPlayer.ticket) {
                    // console.log("=== Current Player Update ===");
                    // console.log("Current player found:", JSON.stringify(currentPlayer, null, 2));
                    // console.log("Setting ticket:", currentPlayer.ticket);
                    const ticketGrid: TicketGrid = {
                        rows: currentPlayer.ticket.map(row => ({numbers: row}))
                    };
                    setTicket(ticketGrid);
                } else {
                    console.log("Current player not found or has no ticket");
                }
            }

            // Update current number only if game is started
            if (state.isGameStarted && state.currentNumber !== null) {
                // console.log("=== Number Called Update ===");
                // console.log("New number called:", state.currentNumber);
                // console.log("Previous called numbers:", calledNumbers);
                setCurrentNumber(state.currentNumber);
                setCalledNumbers(prev => {
                    const newCalledNumbers = [...prev, state.currentNumber];
                    // console.log("Updated called numbers:", newCalledNumbers);
                    return newCalledNumbers;
                });
            }

            // Update called numbers
            if (state.calledNumbers) {
                const calledNumbersArray = Array.from(state.calledNumbers.values());
                // console.log("=== Called Numbers Update ===");
                // console.log("Called numbers from state:", calledNumbersArray);
                // console.log("Previous called numbers:", calledNumbers);
                setCalledNumbers(calledNumbersArray);
            }
        });

        room.onMessage("invalidPrize", data => {
            if (data.playerId === playerId) {
                setErrorMessage(data.reason)
                setErrorCategory(data.category)
            }
        })

        room.onMessage("eligiblePrizes", (message: { playerId: string; prizes: EligiblePrize[] }) => {
            if (message.playerId === playerId) {
                setEligiblePrizes(message.prizes);
            }
        });


        // Listen for game start
        room.onMessage("gameStart", (data) => {
            console.log("=== Game Start Message ===");
            console.log("Game started");
            console.log(data)
            if (room.state) {
                room.state.isGameStarted = true;
                console.log("Game state updated:", room.state);
            }
        });

        // Listen for number called
        room.onMessage("numberCalled", (message) => {
            if (room.state) {
                setCurrentNumber(message.number);
                setCalledNumbers(prev => {
                    const newCalledNumbers = [...prev, message.number];
                    return newCalledNumbers;
                });
            }
        });

        room.onMessage("prizeClaimed", (message) => {
            setPrizeWinners(message.prizeWinners)
            setPrizeWinner({
                playerId: message.playerId,
                prizeId: message.prizeId,
            })
        })

        // Listen for number marked
        room.onMessage("numberMarked", (message) => {
            // Update the UI to show the marked number
            if (room.state) {
                // Update the called numbers list
                setCalledNumbers(prev => {
                    if (!prev.includes(message.number)) {
                        return [...prev, message.number];
                    }
                    return prev;
                });
            }
        });

        room.onMessage("numbersLeft", (message) => {
            setNumbersLeft(message.players)
        })

        // Listen for markNumber response
        room.onMessage("markNumberResponse", (message) => {
            if (message.success) {
                // Update the UI to reflect the marked number
                setCalledNumbers(prev => {
                    if (!prev.includes(message.number)) {
                        return [...prev, message.number];
                    }
                    return prev;
                });
            }
        });

        // Listen for game end
        room.onMessage("gameEnd", () => {
            console.log("=== Game End Message ===");
            console.log("Game ended");
            if (room.state) {
                room.state.isGameStarted = false;
                console.log("Game state updated:", room.state);
            }
        });

        // Listen for game over
        room.onMessage("gameOver", (message) => {
            setGameOver(true)
            console.log("=== Game Over Message ===");
            console.log("Game over message:", message);
            if (message.winner.id === playerId) {
                console.log("Current player has won!");
                setHasWon(true);
            }

            // Call Timepass SDK game over callback
            const timepassSDK = TimepassSDK.getInstance();
            timepassSDK.GameModeFinished();
        });

        // Listen for players update
        room.onMessage("playersList", (message: {
            prizes: Prize[]
            players: Array<{
                playerId: string;
                playerName: string;
                profile: string;
                isReady: boolean;
                isOnline: boolean;
                isBot: boolean;
                sessionId: string;
                ticket: TicketGrid;
                score?: number;
                hasWon?: boolean;
            }>
        }) => {
            // console.log("=== Players List Message ===");
            // console.log("Received players list:", JSON.stringify(message, null, 2));
            if (message.prizes) {
                setPrizes(message.prizes);
            }
            if (message.players && Array.isArray(message.players)) {
                const updatedPlayers: Player[] = message.players.map(player => {
                    console.log(`Processing player ${player.playerName} (${player.playerId})`);
                    // console.log("Player data:", JSON.stringify(player, null, 2));

                    // Convert TicketGrid to 2D grid
                    const ticketGrid: number[][] = [];
                    if (player.ticket && player.ticket.rows) {
                        Array.from(player.ticket.rows).forEach(row => {
                            if (row && row.numbers) {
                                ticketGrid.push(Array.from(row.numbers));
                            }
                        });
                    }

                    const formattedPlayer = {
                        id: player.playerId,
                        name: player.playerName,
                        profile: player.profile,
                        type: player.isBot ? 1 : 0,
                        isReady: player.isReady,
                        isOnline: player.isOnline,
                        sessionId: player.sessionId || '',
                        score: player.score || 0,
                        hasWon: player.hasWon || false,
                        ticket: ticketGrid
                    };
                    // console.log("Formatted player:", JSON.stringify(formattedPlayer, null, 2));
                    return formattedPlayer;
                });

                // console.log("Setting players state:", JSON.stringify(updatedPlayers, null, 2));
                setPlayers(updatedPlayers);

                // Find current player's ticket
                const currentPlayer = updatedPlayers.find(p => p.id === playerId);
                if (currentPlayer && currentPlayer.ticket) {
                    // console.log("=== Current Player Update ===");
                    // console.log("Current player found:", JSON.stringify(currentPlayer, null, 2));
                    // console.log("Setting ticket:", currentPlayer.ticket);
                    const ticketGrid: TicketGrid = {
                        rows: currentPlayer.ticket.map(row => ({numbers: row}))
                    };
                    setTicket(ticketGrid);
                } else {
                    console.log("Current player not found or has no ticket");
                }
            }
        });
    }, [room]);

    const markNumber = (number: number) => {
        if (room) {
            console.log("Marking number:", number);
            room.send("markNumber", {number});

            // Update local state immediately for better UX
            setCalledNumbers(prev => {
                if (!prev.includes(number)) {
                    return [...prev, number];
                }
                return prev;
            });
        }
    };

    const claimPrize = (prizeId: string) => {
        if (room) {
            room.send("claim_prize", {
                prizeId
            });
        }
    }

    const setReady = () => {
        if (room) {
            room.send("ready");
            setIsReady(true);
        }
    };

    return (
        <GameContext.Provider
            value={{
                client,
                room,
                players,
                currentNumber,
                calledNumbers,
                ticket,
                isReady,
                hasWon,
                joinGame,
                markNumber,
                claimPrize,
                setReady,
                prizes,
                prizeWinners,
                errorMessage,
                setErrorMessage,
                numbersLeft,
                prizeWinner,
                setPrizeWinner,
                gameOver,
                errorCategory,
                eligiblePrizes,
                setCheckedNumbers,
                checkedNumbers,
            }}
        >
            {children}
        </GameContext.Provider>
    );
};

export const useGame = () => {
    const context = useContext(GameContext);
    if (context === undefined) {
        throw new Error('useGame must be used within a GameProvider');
    }
    return context;
}; 